// SessionReportsPOC.js — Documentation Fonctionnelle : POC « Rapports de Sessions » (module Rapports & Supervision).
// Aperçu interactif de l'écran réel de rapport de session, fidèle à sa DISPOSITION :
//   - vue LISTE : bandeau héro (compteur, score moyen, Générer, Export, Actualiser), 4 KPIs,
//     barre de filtres (recherche, domaine, module) puis tableau des sessions TERMINÉES
//     (Code/Module, Domaine, Env, Score coloré, Ctrl, Ano, Date, Actions) ;
//   - vue DÉTAIL (rapport) : synthèse serveur (summary), jauge de score de conformité avec
//     la formule appliquée, répartitions des anomalies par sévérité et par statut (donuts +
//     barres « maison »), recommandations de la session, export CSV SIMULÉ (aperçu du fichier).
// Graphiques = divs « maison » (conic-gradient / barres). Actions SIMULÉES localement (aucun appel réseau).
// Source de vérité : scanbank-frontend/src/features/[SCANBANK] Reports/[RPT][01]-SessionReports.js
// + agrégats serveur GET /api/scanbank/reports/sessions/{id}/* et exports /api/scanbank/export/*.
import React, { useState } from 'react';
import {
  FileText, Search, RefreshCw, CheckCircle, XCircle, AlertTriangle, AlertCircle,
  AlertOctagon, Eye, Download, Clock, Target, ArrowLeft, ArrowRight, Info, Printer,
  Shield, Activity, Lightbulb, PieChart, BarChart3, ListChecks, Calculator, Layers,
} from 'lucide-react';
import { Card, CardContent, Badge, EmptyState } from '../ui';

// ---- Seuils & couleurs de score (règle ScanBank : ACCEPTABLE ≥ 85, ALERTE ≥ 70, CRITIQUE sinon)
const scoreColor = (s) => (s == null ? '#9CA3AF' : s >= 85 ? '#10B981' : s >= 70 ? '#F59E0B' : '#EF4444');
const scoreLevel = (s) => (s == null ? '—' : s >= 85 ? 'ACCEPTABLE' : s >= 70 ? 'ALERTE' : 'CRITIQUE');

// ---- Dictionnaires d'énumérations (valeurs backend EXACTES + libellés FR) -----
const SEVERITES = {
  CRITICAL: { label: 'Critique', color: '#EF4444', icon: AlertOctagon, poids: 10 },
  MAJOR: { label: 'Majeure', color: '#F97316', icon: AlertTriangle, poids: 5 },
  MINOR: { label: 'Mineure', color: '#F59E0B', icon: AlertCircle, poids: 1 },
};

const ANOMALIE_STATUTS = {
  OPEN: { label: 'Ouverte', color: '#EF4444' },
  ACKNOWLEDGED: { label: 'Prise en compte', color: '#3B82F6' },
  ESCALATED: { label: 'Escaladée', color: '#F97316' },
  RESOLVED: { label: 'Résolue', color: '#10B981' },
  IGNORED: { label: 'Ignorée', color: '#6B7280' },
};

const RECO_PRIORITES = {
  CRITICAL: { label: 'Critique', color: '#EF4444' },
  HIGH: { label: 'Haute', color: '#F97316' },
  MEDIUM: { label: 'Moyenne', color: '#F59E0B' },
  LOW: { label: 'Basse', color: '#6B7280' },
};

const RECO_STATUTS = {
  PENDING: { label: 'En attente', color: '#F59E0B' },
  ACCEPTED: { label: 'Acceptée', color: '#3B82F6' },
  IN_PROGRESS: { label: 'En cours', color: '#8B5CF6' },
  IMPLEMENTED: { label: 'Implémentée', color: '#10B981' },
  REJECTED: { label: 'Rejetée', color: '#EF4444' },
};

// ---- Données en dur : sessions TERMINÉES + agrégats serveur du rapport ---------
// Chaque session embarque la réponse simulée des endpoints reports/sessions/{id}/* :
// summary (contrôles), conformity (score + niveau), anomalies-by-severity/-by-status,
// recommendations. Scores EXACTS par la formule 100 − (C×10 + M×5 + m×1) ÷ NbContrôles × 10.
const SESSIONS = [
  {
    id: 41,
    sessionCode: 'SES_CBS_AMPLITUDE_HABILITATION_20260708093012_A1B2C3D4',
    module: 'HABILITATION',
    domaine: { code: 'CBS_AMPLITUDE', name: 'Core Banking Amplitude', environment: 'PROD' },
    status: 'COMPLETED',
    sourceFile: 'CBS_AMPLITUDE_HABILITATION_HAB001_PROD_20260708.xml',
    triggeredBy: 'SYSTEM',
    startTime: '08/07/2026 09:30', endTime: '08/07/2026 09:34',
    conformityScore: 67.5, // 100 − (2×10 + 3×5 + 4×1) ÷ 12 × 10 = 67,5
    controles: { total: 12, traites: 45210, echecs: 1289 },
    anomalies: { total: 9, bySeverity: { CRITICAL: 2, MAJOR: 3, MINOR: 4 }, byStatus: { OPEN: 4, ACKNOWLEDGED: 2, ESCALATED: 1, RESOLVED: 2, IGNORED: 0 } },
    recommandations: [
      { id: 101, title: 'Désactiver les comptes génériques à privilèges', priority: 'CRITICAL', status: 'PENDING', suggestedOwner: 'RSSI', dueDate: '15/07/2026' },
      { id: 102, title: 'Mettre en place la revue annuelle des habilitations sensibles', priority: 'CRITICAL', status: 'IN_PROGRESS', suggestedOwner: 'Resp. Habilitations', dueDate: '15/07/2026' },
      { id: 103, title: "Tracer les modifications de profils dans le journal d'audit", priority: 'HIGH', status: 'ACCEPTED', suggestedOwner: 'DSI', dueDate: '23/07/2026' },
      { id: 104, title: 'Supprimer les droits orphelins des utilisateurs sortis', priority: 'HIGH', status: 'PENDING', suggestedOwner: 'Resp. Habilitations', dueDate: '23/07/2026' },
      { id: 105, title: 'Séparer les fonctions de saisie et de validation (SoD)', priority: 'HIGH', status: 'IMPLEMENTED', suggestedOwner: 'RSSI', dueDate: '23/07/2026' },
    ],
  },
  {
    id: 55,
    sessionCode: 'SES_CBS_AMPLITUDE_COMPTABILITE_20260710141005_E5F6G7H8',
    module: 'COMPTABILITE',
    domaine: { code: 'CBS_AMPLITUDE', name: 'Core Banking Amplitude', environment: 'PROD' },
    status: 'COMPLETED',
    sourceFile: 'CBS_AMPLITUDE_COMPTABILITE_CPTA001_PROD_20260710.xml',
    triggeredBy: 'SYSTEM',
    startTime: '10/07/2026 14:10', endTime: '10/07/2026 14:16',
    conformityScore: 90.0, // 100 − (0×10 + 2×5 + 5×1) ÷ 15 × 10 = 90,0
    controles: { total: 15, traites: 128460, echecs: 342 },
    anomalies: { total: 7, bySeverity: { CRITICAL: 0, MAJOR: 2, MINOR: 5 }, byStatus: { OPEN: 2, ACKNOWLEDGED: 1, ESCALATED: 0, RESOLVED: 3, IGNORED: 1 } },
    recommandations: [
      { id: 106, title: 'Justifier les comptes d’attente non apurés à J+30', priority: 'HIGH', status: 'PENDING', suggestedOwner: 'Direction Comptable', dueDate: '25/07/2026' },
      { id: 107, title: 'Automatiser le rapprochement des suspens interbancaires', priority: 'HIGH', status: 'IMPLEMENTED', suggestedOwner: 'Direction Comptable', dueDate: '25/07/2026' },
    ],
  },
  {
    id: 58,
    sessionCode: 'SES_CBS_AMPLITUDE_CREDITS_20260711081420_J9K0L1M2',
    module: 'CREDITS',
    domaine: { code: 'CBS_AMPLITUDE', name: 'Core Banking Amplitude', environment: 'PROD' },
    status: 'COMPLETED',
    sourceFile: 'CBS_AMPLITUDE_CREDITS_CRD004_PROD_20260711.xml',
    triggeredBy: 'SYSTEM',
    startTime: '11/07/2026 08:14', endTime: '11/07/2026 08:19',
    conformityScore: 77.0, // 100 − (1×10 + 2×5 + 3×1) ÷ 10 × 10 = 77,0
    controles: { total: 10, traites: 30875, echecs: 611 },
    anomalies: { total: 6, bySeverity: { CRITICAL: 1, MAJOR: 2, MINOR: 3 }, byStatus: { OPEN: 3, ACKNOWLEDGED: 1, ESCALATED: 0, RESOLVED: 2, IGNORED: 0 } },
    recommandations: [
      { id: 108, title: 'Régulariser les dérogations sur les dossiers crédit hors seuil', priority: 'CRITICAL', status: 'PENDING', suggestedOwner: 'Direction Crédits', dueDate: '18/07/2026' },
      { id: 109, title: 'Compléter les garanties manquantes sur les dossiers actifs', priority: 'HIGH', status: 'ACCEPTED', suggestedOwner: 'Direction Crédits', dueDate: '26/07/2026' },
      { id: 110, title: 'Revoir la cotation des contreparties non notées', priority: 'HIGH', status: 'PENDING', suggestedOwner: 'Direction Risques', dueDate: '26/07/2026' },
    ],
  },
  {
    id: 52,
    sessionCode: 'SES_CBS_BANK_CEMAC_VIREMENTS_20260709163055_N3P4Q5R6',
    module: 'VIREMENTS',
    domaine: { code: 'CBS_BANK_CEMAC', name: 'Core Banking CEMAC', environment: 'PROD' },
    status: 'COMPLETED',
    sourceFile: 'CBS_BANK_CEMAC_VIREMENTS_SECURITY_PROD_20260709.xml',
    triggeredBy: 'admin',
    startTime: '09/07/2026 16:30', endTime: '09/07/2026 16:33',
    conformityScore: 91.3, // 100 − (0×10 + 1×5 + 2×1) ÷ 8 × 10 = 91,25 ≈ 91,3
    controles: { total: 8, traites: 18740, echecs: 96 },
    anomalies: { total: 3, bySeverity: { CRITICAL: 0, MAJOR: 1, MINOR: 2 }, byStatus: { OPEN: 1, ACKNOWLEDGED: 0, ESCALATED: 0, RESOLVED: 2, IGNORED: 0 } },
    recommandations: [
      { id: 111, title: 'Plafonner les virements manuels hors ligne de validation', priority: 'HIGH', status: 'PENDING', suggestedOwner: 'Resp. Flux', dueDate: '24/07/2026' },
    ],
  },
];

const DOMAINES_FILTRE = ['CBS_AMPLITUDE', 'CBS_BANK_CEMAC'];
const MODULES_FILTRE = ['HABILITATION', 'COMPTABILITE', 'CREDITS', 'VIREMENTS'];

// ---- Sous-composants « maison » -------------------------------------------------
// Jauge circulaire de conformité (conic-gradient, pas de SVG/lib).
const ScoreGauge = ({ score, size = 110 }) => {
  const color = scoreColor(score);
  return (
    <div className="relative inline-flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
      <div className="rounded-full" style={{ width: size, height: size, background: `conic-gradient(${color} ${(score || 0) * 3.6}deg, rgba(156,163,175,0.25) 0deg)` }} />
      <div className="absolute rounded-full bg-white dark:bg-gray-800 flex flex-col items-center justify-center" style={{ width: size - 20, height: size - 20 }}>
        <span className="text-2xl font-bold leading-none" style={{ color }}>{score}</span>
        <span className="text-[10px] font-semibold mt-1" style={{ color }}>{scoreLevel(score)}</span>
      </div>
    </div>
  );
};

// Donut multi-segments (conic-gradient) avec total au centre.
const Donut = ({ parts, size = 120, centerLabel }) => {
  const total = parts.reduce((somme, p) => somme + p.value, 0);
  let acc = 0;
  const stops = total > 0
    ? parts.filter((p) => p.value > 0).map((p) => {
        const from = (acc / total) * 360; acc += p.value;
        const to = (acc / total) * 360;
        return `${p.color} ${from.toFixed(1)}deg ${to.toFixed(1)}deg`;
      }).join(', ')
    : 'rgba(156,163,175,0.25) 0deg 360deg';
  return (
    <div className="relative inline-flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
      <div className="rounded-full" style={{ width: size, height: size, background: `conic-gradient(${stops})` }} />
      <div className="absolute rounded-full bg-white dark:bg-gray-800 flex flex-col items-center justify-center" style={{ width: size - 34, height: size - 34 }}>
        <span className="text-xl font-bold text-gray-900 dark:text-white leading-none">{total}</span>
        <span className="text-[9px] text-gray-400 mt-0.5">{centerLabel}</span>
      </div>
    </div>
  );
};

// Barre horizontale « maison » (divs).
const Bar = ({ label, value, max, color }) => (
  <div>
    <div className="flex items-center justify-between text-xs mb-1">
      <span className="text-gray-600 dark:text-gray-300">{label}</span>
      <span className="font-semibold text-gray-800 dark:text-gray-100">{value}</span>
    </div>
    <div className="h-2.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${max > 0 ? Math.min(100, (value / max) * 100) : 0}%`, backgroundColor: color }} />
    </div>
  </div>
);

const KpiCard = ({ icon: Ic, label, value, color }) => (
  <Card className="p-4">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
      </div>
      <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}1A`, color }}>
        <Ic className="h-6 w-6" />
      </div>
    </div>
  </Card>
);

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

// Construit le contenu CSV du rapport d'une session (équivalent /api/scanbank/export).
const construireCsv = (sess) => {
  const lignes = [
    ['Code Session', 'Module', 'Domaine', 'Environnement', 'Statut', 'Score', 'Niveau', 'Contrôles', 'Traités', 'Échecs', 'Anomalies', 'Critiques', 'Majeures', 'Mineures'],
    [sess.sessionCode, sess.module, sess.domaine.name, sess.domaine.environment, sess.status,
      sess.conformityScore, scoreLevel(sess.conformityScore), sess.controles.total, sess.controles.traites,
      sess.controles.echecs, sess.anomalies.total, sess.anomalies.bySeverity.CRITICAL,
      sess.anomalies.bySeverity.MAJOR, sess.anomalies.bySeverity.MINOR],
  ];
  return lignes.map((l) => l.map((c) => `"${c}"`).join(';')).join('\n');
};

// ---- Page ------------------------------------------------------------------------
const SessionReportsPOC = () => {
  const [search, setSearch] = useState('');
  const [filterDomaine, setFilterDomaine] = useState('ALL');
  const [filterModule, setFilterModule] = useState('ALL');
  const [detailId, setDetailId] = useState(null);     // null = vue liste, sinon vue détail
  const [csvApercu, setCsvApercu] = useState('');     // aperçu du CSV « exporté »
  const [toast, setToast] = useState('');

  const notify = (msg) => { setToast(msg); window.setTimeout(() => setToast(''), 2400); };

  const filtered = SESSIONS.filter((sess) => {
    const okSearch = `${sess.sessionCode} ${sess.module} ${sess.domaine.name}`.toLowerCase().includes(search.toLowerCase());
    const okDomaine = filterDomaine === 'ALL' || sess.domaine.code === filterDomaine;
    const okModule = filterModule === 'ALL' || sess.module === filterModule;
    return okSearch && okDomaine && okModule;
  });

  const kpis = {
    total: filtered.length,
    scoreMoyen: filtered.length > 0 ? (filtered.reduce((somme, x) => somme + x.conformityScore, 0) / filtered.length).toFixed(1) : '—',
    anomalies: filtered.reduce((somme, x) => somme + x.anomalies.total, 0),
    recommandations: filtered.reduce((somme, x) => somme + x.recommandations.length, 0),
  };

  const detail = detailId ? SESSIONS.find((x) => x.id === detailId) : null;
  const openDetail = (id) => { setDetailId(id); setCsvApercu(''); };
  const backToList = () => { setDetailId(null); setCsvApercu(''); };

  const exporterCsv = (sess) => {
    setCsvApercu(construireCsv(sess));
    notify(`Export CSV « rapport_${sess.sessionCode}.csv » généré (simulation — streaming serveur dans l'écran réel).`);
  };

  // Décomposition du calcul de score pour la vue détail (pédagogie de la formule)
  const calcul = detail ? (() => {
    const sev = detail.anomalies.bySeverity;
    const penalite = sev.CRITICAL * 10 + sev.MAJOR * 5 + sev.MINOR * 1;
    return { penalite, texte: `100 − ((${sev.CRITICAL}×10 + ${sev.MAJOR}×5 + ${sev.MINOR}×1) ÷ ${detail.controles.total}) × 10 = ${detail.conformityScore}` };
  })() : null;

  const maxSev = detail ? Math.max(1, ...Object.values(detail.anomalies.bySeverity)) : 1;
  const maxStatut = detail ? Math.max(1, ...Object.values(detail.anomalies.byStatus)) : 1;

  return (
    <div className="space-y-8">
      {/* ===== (1) Intro ===== */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Rapports de Sessions</h1>
          <Badge color="#10B981">Module Rapports &amp; Supervision</Badge>
          <Badge color="#3B82F6">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          Aperçu interactif de l'écran <strong>Rapports de Sessions</strong> : sélection d'une session
          terminée puis rapport complet (synthèse, score de conformité, répartitions, recommandations, export).
        </p>
      </div>

      {/* ===== (2) Aperçu interactif ===== */}
      <Card className="overflow-hidden">
        {/* En-tête héro type app (dégradé émeraude) */}
        <div className="px-5 py-4 bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 text-white flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {detail && (
              <button onClick={backToList} className="p-2 rounded-xl bg-white/15 hover:bg-white/25 transition-colors shrink-0">
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <FileText className="h-6 w-6 shrink-0" />
            <div className="min-w-0">
              <h2 className="text-lg font-bold leading-tight truncate">{detail ? 'Rapport de session' : 'Rapports de Sessions'}</h2>
              <p className="text-xs text-white/80 truncate">
                {detail ? detail.sessionCode : 'Sessions d’audit terminées — génération et export de rapports'}
              </p>
            </div>
          </div>
          {!detail && (
            <div className="flex items-center gap-2 shrink-0">
              <span className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/15 text-sm"><FileText className="h-3.5 w-3.5" /><strong>{kpis.total}</strong> sessions</span>
              <span className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff' }}>
                <Target className="h-3.5 w-3.5" />{kpis.scoreMoyen}%
              </span>
              <button onClick={() => notify('Liste rafraîchie (simulation).')} className="p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"><RefreshCw className="h-4 w-4" /></button>
            </div>
          )}
        </div>

        {/* ----- VUE LISTE ----- */}
        {!detail && (
          <div className="p-5 space-y-5">
            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiCard icon={FileText} label="Sessions terminées" value={kpis.total} color="#10B981" />
              <KpiCard icon={Target} label="Score moyen" value={`${kpis.scoreMoyen}%`} color={scoreColor(parseFloat(kpis.scoreMoyen))} />
              <KpiCard icon={AlertTriangle} label="Anomalies détectées" value={kpis.anomalies} color="#EF4444" />
              <KpiCard icon={Lightbulb} label="Recommandations" value={kpis.recommandations} color="#F59E0B" />
            </div>

            {/* Barre de filtres */}
            <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3 flex flex-col lg:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher code, module, domaine…"
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-800 dark:text-gray-100 placeholder-gray-400 focus:ring-2 focus:ring-emerald-500 focus:outline-none" />
              </div>
              <select value={filterDomaine} onChange={(e) => setFilterDomaine(e.target.value)}
                className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100">
                <option value="ALL">Tous domaines</option>
                {DOMAINES_FILTRE.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
              <select value={filterModule} onChange={(e) => setFilterModule(e.target.value)}
                className="px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100">
                <option value="ALL">Tous modules</option>
                {MODULES_FILTRE.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <button onClick={() => notify('Export CSV de la liste généré côté serveur, filtres respectés (simulation).')}
                className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 text-white text-sm font-medium">
                <Download className="h-4 w-4" />Export CSV
              </button>
            </div>

            {/* Tableau des sessions terminées */}
            {filtered.length === 0 ? (
              <EmptyState icon={FileText} title="Aucune session terminée trouvée" message="Modifiez vos filtres pour voir plus de résultats." />
            ) : (
              <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800/60 text-gray-500 dark:text-gray-400 text-xs uppercase tracking-wider">
                    <tr>
                      <th className="text-left font-semibold px-4 py-2.5">Code / Module</th>
                      <th className="text-left font-semibold px-4 py-2.5 hidden md:table-cell">Domaine</th>
                      <th className="text-center font-semibold px-4 py-2.5 hidden lg:table-cell">Env</th>
                      <th className="text-center font-semibold px-4 py-2.5">Score</th>
                      <th className="text-center font-semibold px-4 py-2.5 hidden md:table-cell">Ctrl</th>
                      <th className="text-center font-semibold px-4 py-2.5">Ano</th>
                      <th className="text-center font-semibold px-4 py-2.5 hidden lg:table-cell">Fin</th>
                      <th className="text-right font-semibold px-4 py-2.5">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60 text-gray-700 dark:text-gray-200">
                    {filtered.map((sess) => (
                      <tr key={sess.id} onClick={() => openDetail(sess.id)} className="hover:bg-emerald-50/50 dark:hover:bg-emerald-500/10 cursor-pointer">
                        <td className="px-4 py-3">
                          <p className="font-mono text-xs font-medium text-gray-900 dark:text-white truncate max-w-[280px]">{sess.sessionCode}</p>
                          <p className="text-xs text-gray-500">{sess.module}</p>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-gray-600 dark:text-gray-400">{sess.domaine.name}</td>
                        <td className="px-4 py-3 hidden lg:table-cell text-center">
                          <span className="inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">{sess.domaine.environment}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center justify-center w-9 h-9 rounded-full text-sm font-bold"
                            style={{ backgroundColor: `${scoreColor(sess.conformityScore)}1A`, color: scoreColor(sess.conformityScore) }}>
                            {Math.round(sess.conformityScore)}
                          </span>
                        </td>
                        <td className="px-4 py-3 hidden md:table-cell text-center font-medium">{sess.controles.total}</td>
                        <td className="px-4 py-3 text-center">
                          <span className={`font-medium ${sess.anomalies.total > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500'}`}>{sess.anomalies.total}</span>
                        </td>
                        <td className="px-4 py-3 hidden lg:table-cell text-center text-xs text-gray-500">{sess.endTime}</td>
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center justify-end gap-1">
                            <button onClick={() => openDetail(sess.id)} title="Ouvrir le rapport" className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10"><Eye className="h-4 w-4" /></button>
                            <button onClick={() => { openDetail(sess.id); exporterCsv(sess); }} title="Exporter en CSV" className="p-1.5 rounded-lg text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/10"><Download className="h-4 w-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ----- VUE DÉTAIL (rapport de session) ----- */}
        {detail && (
          <div className="p-5 space-y-5">
            {/* En-tête détail + actions */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white font-mono truncate">{detail.sessionCode}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {detail.module} • {detail.domaine.name} • terminée le {detail.endTime}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge color="#10B981"><CheckCircle className="h-3 w-3" />Terminée</Badge>
                <button onClick={() => exporterCsv(detail)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 text-white text-sm font-medium">
                  <Download className="h-4 w-4" />Exporter CSV
                </button>
                <button onClick={() => notify('Rapport ouvert dans une nouvelle fenêtre pour impression PDF (simulation).')}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <Printer className="h-4 w-4" />Imprimer PDF
                </button>
              </div>
            </div>

            {/* Ligne du haut : Synthèse / Score de conformité / Informations */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-700">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2"><Shield className="h-3.5 w-3.5" />Synthèse (summary)</h4>
                <div className="space-y-2">
                  {[
                    [Layers, 'Contrôles exécutés', detail.controles.total],
                    [Activity, 'Enregistrements traités', detail.controles.traites.toLocaleString('fr-FR')],
                    [XCircle, 'Échecs relevés', detail.controles.echecs.toLocaleString('fr-FR')],
                    [AlertTriangle, 'Anomalies détectées', detail.anomalies.total],
                    [Clock, 'Durée du pipeline', `${detail.startTime.split(' ')[1]} → ${detail.endTime.split(' ')[1]}`],
                  ].map(([Ic, l, v], i) => (
                    <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-700/40 text-sm">
                      <span className="flex items-center gap-2 text-gray-500 dark:text-gray-400"><Ic className="h-4 w-4" />{l}</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{v}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-700 flex flex-col items-center justify-center text-center">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2"><Target className="h-3.5 w-3.5" />Score de conformité</h4>
                <ScoreGauge score={detail.conformityScore} size={110} />
                <p className="text-[11px] font-mono text-gray-500 dark:text-gray-400 mt-3 px-2">{calcul.texte}</p>
                <p className="text-[10px] text-gray-400 mt-1">Pénalité pondérée : {calcul.penalite} point(s) sur {detail.controles.total} contrôles</p>
              </div>

              <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-700">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2"><Info className="h-3.5 w-3.5" />Informations</h4>
                <div className="space-y-2.5 text-sm">
                  {[
                    ['Module', detail.module],
                    ['Domaine', detail.domaine.name],
                    ['Environnement', detail.domaine.environment],
                    ['Fichier source', detail.sourceFile],
                    ['Déclenchée par', detail.triggeredBy],
                    ['Début', detail.startTime],
                    ['Fin', detail.endTime],
                  ].map(([l, v], i) => (
                    <div key={i} className="flex justify-between gap-3">
                      <span className="text-gray-500 dark:text-gray-400 shrink-0">{l}</span>
                      <span className="font-medium text-gray-900 dark:text-white text-right truncate text-xs">{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Répartitions : par sévérité + par statut */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-700">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2"><PieChart className="h-3.5 w-3.5" />Anomalies par sévérité</h4>
                <div className="flex items-center gap-5 flex-wrap">
                  <Donut centerLabel="anomalies" parts={Object.entries(SEVERITES).map(([k, c]) => ({ value: detail.anomalies.bySeverity[k] || 0, color: c.color }))} />
                  <div className="flex-1 min-w-[180px] space-y-3">
                    {Object.entries(SEVERITES).map(([k, c]) => (
                      <Bar key={k} label={`${c.label} (poids ×${c.poids})`} value={detail.anomalies.bySeverity[k] || 0} max={maxSev} color={c.color} />
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-700">
                <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2"><BarChart3 className="h-3.5 w-3.5" />Anomalies par statut</h4>
                <div className="flex items-center gap-5 flex-wrap">
                  <Donut centerLabel="anomalies" parts={Object.entries(ANOMALIE_STATUTS).map(([k, c]) => ({ value: detail.anomalies.byStatus[k] || 0, color: c.color }))} />
                  <div className="flex-1 min-w-[180px] space-y-3">
                    {Object.entries(ANOMALIE_STATUTS).map(([k, c]) => (
                      <Bar key={k} label={c.label} value={detail.anomalies.byStatus[k] || 0} max={maxStatut} color={c.color} />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Recommandations de la session */}
            <div className="p-5 rounded-xl border border-gray-200 dark:border-gray-700">
              <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Lightbulb className="h-3.5 w-3.5" />Recommandations de la session
                <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 dark:bg-amber-500/20 text-amber-600 dark:text-amber-400">{detail.recommandations.length}</span>
              </h4>
              <div className="space-y-2">
                {detail.recommandations.map((r) => {
                  const pr = RECO_PRIORITES[r.priority] || RECO_PRIORITES.LOW;
                  const st = RECO_STATUTS[r.status] || RECO_STATUTS.PENDING;
                  return (
                    <div key={r.id} className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/40 border border-gray-100 dark:border-gray-700/60 flex-wrap">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${pr.color}1A`, color: pr.color }}>
                        <Lightbulb className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-[220px]">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">{r.title}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{r.suggestedOwner} • échéance {r.dueDate}</p>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Badge color={pr.color}>{pr.label}</Badge>
                        <Badge color={st.color}>{st.label}</Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Aperçu du CSV « exporté » (simulation) */}
            {csvApercu && (
              <div className="p-5 rounded-xl border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-500/10">
                <h4 className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider mb-3 flex items-center gap-2">
                  <Download className="h-3.5 w-3.5" />Aperçu du fichier CSV généré (simulation)
                </h4>
                <pre className="text-[11px] font-mono text-gray-700 dark:text-gray-200 overflow-x-auto whitespace-pre bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">{csvApercu}</pre>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Dans l'écran réel, l'export est un flux CSV serveur (Content-Disposition horodaté) qui respecte les filtres actifs.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Toast simulé */}
        {toast && (
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <Info className="h-4 w-4 text-emerald-500" />{toast}
          </div>
        )}
      </Card>

      {/* ===== (3) Guide utilisateur ===== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Search className="h-5 w-5 text-blue-500" />1. Sélectionner une session</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Seules les sessions <strong>terminées</strong> (COMPLETED) apparaissent : un rapport n'existe qu'en fin de pipeline.</GuideStep>
              <GuideStep n={2}>Filtrez par <strong>recherche</strong>, <strong>domaine</strong> ou <strong>module CBS</strong>.</GuideStep>
              <GuideStep n={3}>Cliquez sur une ligne pour ouvrir le <strong>rapport</strong>. <button onClick={() => openDetail(41)} className="text-blue-600 dark:text-blue-400 underline">Essayer (session à 67,5)</button></GuideStep>
            </ol>
          </CardContent></Card>
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Target className="h-5 w-5 text-blue-500" />2. Analyser le rapport</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>La <strong>synthèse</strong> vient des agrégats serveur : contrôles exécutés, enregistrements traités, échecs.</GuideStep>
              <GuideStep n={2}>La <strong>jauge de conformité</strong> affiche le score, son niveau (CRITIQUE / ALERTE / ACCEPTABLE) et le calcul appliqué.</GuideStep>
              <GuideStep n={3}>Les <strong>donuts</strong> répartissent les anomalies par sévérité et par statut de résolution. <button onClick={() => openDetail(55)} className="text-blue-600 dark:text-blue-400 underline">Voir (session à 90,0)</button></GuideStep>
            </ol>
          </CardContent></Card>
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Download className="h-5 w-5 text-blue-500" />3. Exporter</h3>
            <ol className="space-y-2">
              <GuideStep n={1}><strong>Exporter CSV</strong> génère le rapport côté serveur (flux CSV horodaté). <button onClick={() => { const cible = detail || SESSIONS[0]; if (!detail) openDetail(cible.id); exporterCsv(cible); }} className="text-blue-600 dark:text-blue-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}><strong>Imprimer PDF</strong> ouvre une version imprimable du rapport dans une nouvelle fenêtre.</GuideStep>
              <GuideStep n={3}>L'écran réel propose aussi les formats <strong>Excel</strong> et <strong>JSON</strong>, et l'export en lot d'une sélection multi-pages.</GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== (4) Règles de gestion ===== */}
      <Card><CardContent>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Calculator className="h-5 w-5 text-blue-500" />Règles de gestion — scoring &amp; rapport</h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700">
            <p className="text-sm font-mono text-gray-800 dark:text-gray-100">Score = 100 − (CRITICAL×10 + MAJOR×5 + MINOR×1) ÷ NbContrôles × 10</p>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              Exemple (session HABILITATION) : 100 − ((2×10 + 3×5 + 4×1) ÷ 12) × 10 = <strong>67,5</strong> → niveau <strong>CRITIQUE</strong>.
              Score borné à [0, 100], calculé à l'étape SCORING du workflow SCANBANK_AUDIT_SESSION et persisté
              dans <code>audit_session.conformity_score</code>.
            </p>
          </div>
          <div className="space-y-2">
            {[
              ['CRITIQUE', '#EF4444', 'score < 70'],
              ['ALERTE', '#F59E0B', '70 ≤ score < 85'],
              ['ACCEPTABLE', '#10B981', 'score ≥ 85'],
            ].map(([lvl, color, desc]) => (
              <div key={lvl} className="flex items-center gap-3 p-2.5 rounded-lg border border-gray-200 dark:border-gray-700">
                <Badge color={color}>{lvl}</Badge>
                <span className="text-xs font-mono text-gray-600 dark:text-gray-300">{desc}</span>
                <span className="text-xs text-gray-400 ml-auto">niveau renvoyé par /conformity</span>
              </div>
            ))}
          </div>
        </div>
        <ul className="mt-4 space-y-1.5 text-sm text-gray-600 dark:text-gray-300 list-disc list-inside">
          <li>Les recommandations sont <strong>générées automatiquement</strong> à l'intégration pour les anomalies CRITICAL et MAJOR, avec échéances par priorité : critique 7 j, haute 15 j, moyenne 30 j, basse 60 j.</li>
          <li>Le rapport s'appuie d'abord sur la <strong>vérité serveur</strong> (endpoints reports) avec repli sur les données de la session si le backend reports est indisponible (aucune régression).</li>
          <li>Les exports « pleine liste » sont générés <strong>côté serveur en flux CSV</strong> et respectent les filtres actifs (la table complète n'est jamais téléchargée côté client).</li>
        </ul>
      </CardContent></Card>

      {/* ===== (5) Endpoints ===== */}
      <Card><CardContent>
        <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><ListChecks className="h-5 w-5 text-blue-500" />Endpoints API</h3>
        <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
              <tr>{['Méthode', 'Endpoint', 'Usage', 'Permission'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
              {[
                ['/api/scanbank/reports/sessions/{id}/summary', 'Synthèse contrôles & anomalies', 'SCANBANK_REPORT_VIEW'],
                ['/api/scanbank/reports/sessions/{id}/conformity', 'Score + niveau CRITIQUE/ALERTE/ACCEPTABLE', 'SCANBANK_REPORT_VIEW'],
                ['/api/scanbank/reports/sessions/{id}/anomalies-by-severity', 'Répartition par sévérité', 'SCANBANK_REPORT_VIEW'],
                ['/api/scanbank/reports/sessions/{id}/anomalies-by-status', 'Répartition par statut', 'SCANBANK_REPORT_VIEW'],
                ['/api/scanbank/reports/sessions/{id}/recommendations', 'Recommandations + taux d’implémentation', 'SCANBANK_REPORT_VIEW'],
                ['/api/scanbank/export/sessions?format=csv&status=COMPLETED', 'Export CSV des sessions (flux serveur)', 'SCANBANK_AUDIT_VIEW'],
                ['/api/scanbank/export/anomalies?format=csv&sessionId={id}', 'Export CSV des anomalies', 'SCANBANK_ANOMALY_VIEW'],
                ['/api/scanbank/export/recommendations?format=csv', 'Export CSV des recommandations', 'SCANBANK_RECOMMENDATION_VIEW'],
                ['/api/scanbank/audit/sessions/page?status=COMPLETED&search=&domainCode=&module=', 'Liste paginée des sessions terminées', 'SCANBANK_AUDIT_VIEW'],
              ].map((r, i) => (
                <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2"><Badge color="#10B981">GET</Badge></td>
                  <td className="px-4 py-2 font-mono text-xs break-all">{r[0]}</td>
                  <td className="px-4 py-2 text-xs">{r[1]}</td>
                  <td className="px-4 py-2 font-mono text-[11px]">{r[2]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-gray-400 mt-3 flex items-center gap-1">
          <ArrowRight className="h-3 w-3" />Source : écran réel <code>[RPT][01]-SessionReports.js</code> + ReportController / ExportController (scanbank_core).
        </p>
      </CardContent></Card>
    </div>
  );
};

export default SessionReportsPOC;
